import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { getOnline } from '../lib/offline/networkStatus';
import {
  readStoredSession,
  readCachedProfile,
  cacheProfile,
  clearCachedProfile,
} from '../lib/auth/cachedSession';
import { clearCurrentUserPendingEmails } from '../lib/undoSend';

const AuthContext = createContext(null);

/**
 * AuthProvider — wraps the entire app.
 *
 * Provides: { user, profile, role, loading, signIn, signOut, refreshProfile }
 *
 * `profile` comes from public.user_profiles (role, display_name, airtable_person_id).
 * `role` is a convenience shortcut to profile.role (defaults to null).
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch user_profiles row for given user id
  const fetchProfile = useCallback(async (userId) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (error) {
        console.error('[Auth] Profile fetch error:', error.message);
        return null;
      }
      // Keep a copy so role/display_name survive an offline cold-boot.
      cacheProfile(data);
      return data;
    } catch (err) {
      console.error('[Auth] Profile fetch exception:', err);
      return null;
    }
  }, []);

  // Initialise session on mount
  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user && mounted) {
          setUser(session.user);
          let p = await fetchProfile(session.user.id);
          // Offline (valid token < 1h): the profile fetch hits the network and
          // returns null — fall back to the last-known cached role.
          if (!p && !getOnline()) p = readCachedProfile(session.user.id);
          if (mounted) setProfile(p);
        } else if (mounted && !getOnline()) {
          // OFFLINE COLD-BOOT (installed PWA reopened on a plane). getSession()
          // returned nothing because the expired access token could not be
          // refreshed without a network. Do NOT bounce to <Login> — Supabase
          // can't authenticate offline, so the login screen is a dead end in
          // flight. Rehydrate from the session Supabase already persisted plus
          // the cached profile so the app opens to its offline-cached data.
          // When connectivity returns, supabase-js refreshes the token (or
          // genuinely signs out if the refresh token is invalid) on its own.
          const stored = readStoredSession();
          if (stored?.user) {
            setUser(stored.user);
            setProfile(readCachedProfile(stored.user.id));
          }
        }
      } catch (err) {
        // Supabase Web Locks can fail — log but don't block the app. If we're
        // offline, still try to rehydrate from the persisted session so a
        // transient init throw doesn't strand the user on the login screen.
        console.warn('[Auth] Init error (continuing):', err.message?.substring(0, 80));
        if (mounted && !getOnline()) {
          const stored = readStoredSession();
          if (stored?.user) {
            setUser(stored.user);
            setProfile(readCachedProfile(stored.user.id));
          }
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    init();

    // Listen for auth state changes (login, logout, token refresh).
    // CRITICAL: this callback MUST be synchronous and MUST NOT await any
    // Supabase call. supabase-js invokes it from INSIDE GoTrueClient's auth
    // lock (during _initialize / _callRefreshToken -> _notifyAllSubscribers).
    // Awaiting a DB query here (fetchProfile -> supabase.from('user_profiles'))
    // makes that query wait on the same lock the refresh holds -> permanent
    // deadlock: every getSession() then stalls and apiFetch 401s app-wide
    // (UI stuck on "Saving..." / "Loading..."). Proven 2026-05-28
    // (TICKET-FILES-SAVE-HANG): console stack getSession -> _acquireLock ->
    // _initialize -> _recoverAndRefresh -> _callRefreshToken ->
    // _notifyAllSubscribers -> this callback. Fix: defer the profile fetch with
    // setTimeout(0) so it runs AFTER the lock releases. supabase-js documented
    // anti-pattern. Tested: tests/auth/AuthContext-onauthstatechange-defer.test.jsx
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;

        if (event === 'SIGNED_IN' && session?.user) {
          setUser(session.user);
          // Deferred OUT of the auth lock — never await Supabase here.
          // setLoading(false) is called inside the deferred callback so the app
          // doesn't briefly mount with role=null (treated as CEO) before the
          // profile resolves.
          setTimeout(() => {
            if (!mounted) return;
            fetchProfile(session.user.id).then((p) => {
              if (mounted) {
                setProfile(p);
                setLoading(false);
              }
            });
          }, 0);
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setProfile(null);
          setLoading(false);
        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
          setUser(session.user);
          setLoading(false);
        } else {
          setLoading(false);
        }
      }
    );

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, [fetchProfile]);

  // Sign in with email + password
  const signIn = async (email, password) => {
    setLoading(true);
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setLoading(false);
      return { error };
    }
    if (data.user) {
      setUser(data.user);
      const p = await fetchProfile(data.user.id);
      setProfile(p);
    }
    setLoading(false);
    return { error: null };
  };

  // Sign out
  const signOut = async () => {
    setLoading(true);
    // Clear pending emails so the next user doesn't see the previous user's drafts (TICKET-964)
    clearCurrentUserPendingEmails();
    // Clear CEO role override + cached offline profile so next login starts fresh
    try { localStorage.removeItem('acn_role_override'); } catch {}
    clearCachedProfile();
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    setLoading(false);
  };

  // Refresh profile (e.g. after role update)
  const refreshProfile = async () => {
    if (user) {
      const p = await fetchProfile(user.id);
      setProfile(p);
    }
  };

  const value = {
    user,
    profile,
    role: profile?.role || null,
    loading,
    signIn,
    signOut,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * useAuth() hook — access auth state anywhere.
 *
 * const { user, profile, role, loading, signIn, signOut } = useAuth();
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}

export default AuthContext;
